import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/axios';

const EVENTS = [
  {
    key: 'order_create_template',
    label: 'Order placed',
    desc: 'Sends the moment a customer checks out',
    icon: '🛒',
    bg: '#eaf3de',
    text: '#27500a',
    liveBg: '#639922',
    variables: [
      { slot: '{{1}}', label: 'Customer Name', field: 'customer_name' },
      { slot: '{{2}}', label: 'Order Number', field: 'order_number' },
      { slot: '{{3}}', label: 'Total Amount', field: 'total_amount' },
    ],
  },
  {
    key: 'order_fulfilled_template',
    label: 'Order shipped',
    desc: 'Sends when fulfillment is created',
    icon: '🚚',
    bg: '#faeeda',
    text: '#633806',
    liveBg: '#ba7517',
    variables: [
      { slot: '{{1}}', label: 'Customer Name', field: 'customer_name' },
      { slot: '{{2}}', label: 'Order Number', field: 'order_number' },
      { slot: '{{3}}', label: 'Tracking Number', field: 'tracking_number' },
      { slot: '{{4}}', label: 'Courier', field: 'courier' },
    ],
  },
  {
    key: 'abandon_cart_template',
    label: 'Cart abandoned',
    desc: 'Sends after a checkout is left incomplete',
    icon: '🧺',
    bg: '#faece7',
    text: '#712b13',
    liveBg: '#d85a30',
    variables: [
      { slot: '{{1}}', label: 'Customer Name', field: 'customer_name' },
      { slot: '{{2}}', label: 'Cart Link', field: 'cart_url' },
      { slot: '{{3}}', label: 'Total Amount', field: 'total_amount' },
    ],
  },
  {
    key: 'order_cancelled_template',
    label: 'Order cancelled',
    desc: 'Sends when an order is cancelled',
    icon: '❌',
    bg: '#fde8e8',
    text: '#7a1212',
    liveBg: '#c0392b',
    variables: [
      { slot: '{{1}}', label: 'Customer Name', field: 'customer_name' },
      { slot: '{{2}}', label: 'Order Number', field: 'order_number' },
    ],
  },
  {
    key: 'order_paid_template',
    label: 'Order paid (COD)',
    desc: 'Sends when payment is confirmed',
    icon: '💰',
    bg: '#e8f4fd',
    text: '#0a3d6b',
    liveBg: '#2980b9',
    variables: [
      { slot: '{{1}}', label: 'Customer Name', field: 'customer_name' },
      { slot: '{{2}}', label: 'Order Number', field: 'order_number' },
      { slot: '{{3}}', label: 'Total Amount', field: 'total_amount' },
    ],
  },
  {
    key: 'refund_template',
    label: 'Refund initiated',
    desc: 'Sends when a refund is created',
    icon: '↩️',
    bg: '#f0e8fd',
    text: '#3d0a6b',
    liveBg: '#8e44ad',
    variables: [
      { slot: '{{1}}', label: 'Customer Name', field: 'customer_name' },
      { slot: '{{2}}', label: 'Order Number', field: 'order_number' },
      { slot: '{{3}}', label: 'Refund Amount', field: 'refund_amount' },
    ],
  },
];

export default function NotificationSettings() {
  const { selectedBusiness, user } = useAuth();
  const bizId = user?.role === 'admin' ? selectedBusiness?.id : user?.business_id;
  const [waTemplates, setWaTemplates] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [previewMap, setPreviewMap] = useState({});
  const [testPhone, setTestPhone] = useState('');
  const [testStatus, setTestStatus] = useState({});
  const [webhookStatus, setWebhookStatus] = useState(null);

  useEffect(() => { if (bizId) fetchAll(); }, [bizId]);

  async function fetchAll() {
    setLoading(true);
    try {
      const [tmplRes, notifyRes] = await Promise.all([
        api.get('/templates?business_id=' + bizId),
        api.get('/shopify/notification-settings'),
      ]);
      const approved = (tmplRes.data || []).filter(t => (t.status || '').toUpperCase() === 'APPROVED');
      setWaTemplates(approved);
      setSettings({
        order_create_template: notifyRes.data?.order_create_template || '',
        order_fulfilled_template: notifyRes.data?.order_fulfilled_template || '',
        abandon_cart_template: notifyRes.data?.abandon_cart_template || '',
      });
      const pm = {};
      approved.forEach(t => { pm[t.name] = t; });
      setPreviewMap(pm);
      try {
        const wh = await api.get('/shopify/webhook-status');
        setWebhookStatus(wh.data);
      } catch (e) { setWebhookStatus(null); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function save() {
    setSaving(true); setSaved(false);
    try {
      await api.put('/shopify/notification-settings', settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { console.error(e); }
    setSaving(false);
  }

  async function sendTest(eventKey) {
    if (!testPhone) { alert('Phone number தேவை (91XXXXXXXXXX)'); return; }
    const tmplName = settings[eventKey];
    if (!tmplName) { alert('Template select பண்ணவும்'); return; }
    setTestStatus(p => ({ ...p, [eventKey]: 'sending' }));
    try {
      await api.post('/shopify/test-notification', { event: eventKey, template: tmplName, phone: testPhone, business_id: bizId });
      setTestStatus(p => ({ ...p, [eventKey]: 'sent' }));
      setTimeout(() => setTestStatus(p => ({ ...p, [eventKey]: '' })), 3000);
    } catch (e) {
      setTestStatus(p => ({ ...p, [eventKey]: 'error' }));
      setTimeout(() => setTestStatus(p => ({ ...p, [eventKey]: '' })), 3000);
    }
  }

  function getTemplateBody(tmplName) {
    const t = previewMap[tmplName];
    if (!t) return null;
    try {
      const comps = typeof t.components === 'string' ? JSON.parse(t.components) : t.components;
      const body = (comps || []).find(c => c.type === 'BODY');
      return body?.text || null;
    } catch { return null; }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading...</div>;

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'DM Sans, sans-serif', minHeight: '100vh', background: '#f6f8fa' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111', margin: 0 }}>Notifications</h2>
        <p style={{ fontSize: 14, color: '#9ca3af', marginTop: 4 }}>Map a WhatsApp template to each Shopify event. Messages send automatically — no code changes needed.</p>
      </div>

      {webhookStatus && (
        <div style={{ background: '#fff', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #e5e7eb' }}>
          <span style={{ fontSize: 18 }}>{webhookStatus.registered ? '✅' : '⚠️'}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: webhookStatus.registered ? '#166534' : '#92400e' }}>
              Shopify Webhooks {webhookStatus.registered ? 'Active' : 'Not Registered'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{webhookStatus.registered ? 'Order events are being received.' : 'Click below to register webhooks.'}</div>
          </div>
          {!webhookStatus.registered && (
            <button onClick={async () => { await api.post('/shopify/register-webhooks'); fetchAll(); }}
              style={{ marginLeft: 'auto', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
              Register
            </button>
          )}
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 10, padding: '12px 16px', marginBottom: 20, border: '1px solid #e5e7eb', display: 'flex', gap: 10, alignItems: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>📱 Test Phone:</span>
        <input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="91XXXXXXXXXX"
          style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 13 }} />
      </div>

      {EVENTS.map(ev => {
        const selected = settings[ev.key] || '';
        const isLive = !!selected;
        const preview = selected ? getTemplateBody(selected) : null;
        const tmplObj = previewMap[selected];
        const ts = testStatus[ev.key];
        return (
          <div key={ev.key} style={{ background: ev.bg, borderRadius: 14, padding: '18px 20px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22 }}>{ev.icon}</span>
                <div>
                  <p style={{ fontSize: 15, fontWeight: 700, color: ev.text, margin: 0 }}>{ev.label}</p>
                  <p style={{ fontSize: 12.5, color: '#6b7280', margin: 0 }}>{ev.desc}</p>
                </div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 12px', borderRadius: 20, background: isLive ? ev.liveBg : '#fff', color: isLive ? '#fff' : '#9ca3af' }}>
                {isLive ? 'Live' : 'Not set'}
              </span>
            </div>
            <select value={selected} onChange={e => setSettings(p => ({ ...p, [ev.key]: e.target.value }))}
              style={{ width: '100%', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 8, padding: '0.6rem 0.75rem', fontSize: 13.5, background: '#fff', marginBottom: 10 }}>
              <option value="">Select a template</option>
              {waTemplates.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
            {tmplObj && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                <span style={{ fontSize: 11, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 20, padding: '3px 10px', color: '#374151' }}>Category: {tmplObj.category}</span>
                <span style={{ fontSize: 11, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 20, padding: '3px 10px', color: '#374151' }}>Language: {tmplObj.language}</span>
              </div>
            )}
            {selected && (
              <div style={{ background: 'rgba(255,255,255,0.6)', borderRadius: 8, padding: '10px 14px', marginBottom: 10 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#374151', margin: '0 0 8px 0' }}>📌 Variable Mapping</p>
                {ev.variables.map(v => (
                  <div key={v.slot} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, background: ev.liveBg, color: '#fff', borderRadius: 6, padding: '2px 8px', minWidth: 36, textAlign: 'center' }}>{v.slot}</span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>→</span>
                    <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{v.label}</span>
                  </div>
                ))}
              </div>
            )}
            {preview && (
              <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '10px 14px', marginBottom: 10, borderLeft: `3px solid ${ev.liveBg}` }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', margin: '0 0 4px 0' }}>PREVIEW</p>
                <p style={{ fontSize: 12.5, color: '#374151', margin: 0, whiteSpace: 'pre-line' }}>{preview}</p>
              </div>
            )}
            {selected && (
              <button onClick={() => sendTest(ev.key)} disabled={ts === 'sending'}
                style={{ background: ts === 'sent' ? '#166534' : ts === 'error' ? '#991b1b' : ev.liveBg, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
                {ts === 'sending' ? '⏳ Sending...' : ts === 'sent' ? '✅ Sent!' : ts === 'error' ? '❌ Failed' : '📤 Send Test'}
              </button>
            )}
          </div>
        );
      })}

      <button onClick={save} disabled={saving}
        style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: saved ? '#166534' : '#25D366', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 }}>
        {saving ? 'Saving...' : saved ? '✅ Saved!' : 'Save all changes'}
      </button>
    </div>
  );
}
